// AlarmSummaryCard — Phase 2 of the Site Overview redesign (djin-cloud#67).
//
// Row 2 right-hand card (col-span 4 on desktop). Shows:
//   1. Header: unacknowledged count ("N need attention") + optional acknowledged count in gray.
//   2. 3-tier severity grid (critical | warning | info).
//      The site-detail alarm_summary on /api/v1/projects/:id returns a 4-tier
//      count split (critical / high / medium / low / emergency). The UI here
//      collapses it to the 3-tier contract the cloud alarm overhaul settled
//      on:
//        critical = critical_count + emergency_count
//        warning  = high_count + medium_count
//        info     = low_count
//      Ref: design doc §4.6 and the 3-tier severity type in alarm.types.ts.
//      #433: these severity counts are Needs-Attention-scoped (active AND
//      unacknowledged) at the source — an acked-but-present alarm contributes 0,
//      so the tiles agree with the "N need attention" header instead of the
//      old "0 NEED ATTENTION yet 11 critical" mismatch.
//   3. Top-5 Needs-Attention alarms from the per-site alarms endpoint (read-only).
//   4. Deep-link footer to the dedicated alarms page.
//
// Pane-of-glass: no ack/clear/resolve buttons. Status mutations happen at the
// local HMI and propagate via edge-emitted transitions. See
// project_cloud_alarms_readonly_mirror.md.
//
// Data source: `alarmsApi.listForSite(siteId, { status: 'needs_attention' })`
// via React Query (#433 — acked rows live only in the Acknowledged view). The
// endpoint does not expose a severity-sort param; client-side sort
// (critical > warning > info) keeps the list useful at small N.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { alarmsApi } from '../../../services/alarmsApi';
import type { AlarmSummary } from '../../../types/project.types';
import type { ActiveAlarm, Severity } from '../../../types/alarm.types';

export interface AlarmSummaryCardProps {
  siteId: string;
  alarmSummary: AlarmSummary;
}

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const SEVERITY_DOT: Record<Severity, string> = {
  critical: 'bg-ems-red',
  warning: 'bg-ems-amber',
  info: 'bg-ems-blue',
};

function collapseSeverity(summary: AlarmSummary): {
  critical: number;
  warning: number;
  info: number;
} {
  return {
    critical: (summary.critical_count ?? 0) + (summary.emergency_count ?? 0),
    warning: (summary.high_count ?? 0) + (summary.medium_count ?? 0),
    info: summary.low_count ?? 0,
  };
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true });
}

interface SeverityTileProps {
  testId: string;
  count: number;
  label: string;
  countClass: string;
  labelClass: string;
  bgClass: string;
}

function SeverityTile({ testId, count, label, countClass, labelClass, bgClass }: SeverityTileProps) {
  return (
    <div className={`${bgClass} rounded p-2 text-center`}>
      <div data-testid={testId} className={`text-xl font-mono font-bold tabular-nums ${countClass}`}>
        {count}
      </div>
      <div className={`text-[9px] uppercase tracking-wider ${labelClass}`}>{label}</div>
    </div>
  );
}

export function AlarmSummaryCard({ siteId, alarmSummary }: AlarmSummaryCardProps) {
  const counts = useMemo(() => collapseSeverity(alarmSummary), [alarmSummary]);

  const alarmsQuery = useQuery({
    queryKey: ['alarms', siteId, 'list', { status: 'needs_attention' }],
    queryFn: () => alarmsApi.listForSite(siteId, { status: 'needs_attention' }),
    staleTime: 10_000,
    enabled: !!siteId,
  });

  const topFive: ActiveAlarm[] = useMemo(() => {
    const all = alarmsQuery.data?.alarms ?? [];
    // Client-side sort by severity (critical first); endpoint has no sort param.
    return [...all]
      .sort((a, b) => {
        const rankDiff = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
        if (rankDiff !== 0) return rankDiff;
        // Tie-break: more recent first.
        return new Date(b.raised_at).getTime() - new Date(a.raised_at).getTime();
      })
      .slice(0, 5);
  }, [alarmsQuery.data]);

  return (
    <section className="card rounded-xl border border-ems-border-bright/50 overflow-hidden bg-ems-bg-card/60 h-full flex flex-col">
      <div className="px-5 py-3 flex items-start justify-between gap-2 border-b border-ems-border">
        <h3 className="text-sm font-semibold text-ems-text-primary tracking-wide uppercase">Alarms</h3>
        <div className="text-right leading-tight">
          <div className="text-[10px] text-ems-text-muted uppercase tracking-wider">
            {alarmSummary.unacknowledged_count} need attention
          </div>
          {alarmSummary.acknowledged_count > 0 && (
            <div className="text-[10px] text-ems-text-muted/50 uppercase tracking-wider">
              {alarmSummary.acknowledged_count} acknowledged
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {/* 3-tier severity counts */}
        <div className="grid grid-cols-3 gap-2">
          <SeverityTile
            testId="alarm-count-critical"
            count={counts.critical}
            label="Critical"
            bgClass="bg-ems-red/10 border border-ems-red/30"
            countClass="text-ems-red"
            labelClass="text-ems-red/80"
          />
          <SeverityTile
            testId="alarm-count-warning"
            count={counts.warning}
            label="Warning"
            bgClass="bg-ems-amber/10 border border-ems-amber/30"
            countClass="text-ems-amber"
            labelClass="text-ems-amber/80"
          />
          <SeverityTile
            testId="alarm-count-info"
            count={counts.info}
            label="Info"
            bgClass="bg-ems-blue/10 border border-ems-blue/30"
            countClass="text-ems-blue"
            labelClass="text-ems-blue/80"
          />
        </div>

        {/* Top-5 Needs-Attention list */}
        <div className="flex-1">
          {alarmsQuery.isLoading && (
            <div className="space-y-1.5" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-8 rounded bg-ems-bg-card/40 animate-pulse border border-ems-border/40"
                />
              ))}
            </div>
          )}

          {alarmsQuery.isError && (
            <div className="text-xs text-ems-text-muted py-2">Unable to load alarms</div>
          )}

          {!alarmsQuery.isLoading && !alarmsQuery.isError && topFive.length === 0 && (
            <div className="text-xs text-ems-text-muted py-4 text-center">Nothing needs attention</div>
          )}

          {!alarmsQuery.isLoading && topFive.length > 0 && (
            <div className="space-y-1.5">
              {topFive.map((a, idx) => (
                <div
                  key={`${a.device_id}:${a.condition_key}`}
                  className={`flex items-start gap-2 py-1.5 ${
                    idx < topFive.length - 1 ? 'border-b border-ems-border/40' : ''
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${SEVERITY_DOT[a.severity]}`}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-ems-text-primary truncate">
                      {a.condition_label}
                    </div>
                    <div className="text-[10px] text-ems-text-muted">
                      {a.device_id}
                      {a.raised_at ? ` · ${relativeTime(a.raised_at)}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Link
          to={`/project/${siteId}/alarms`}
          className="block text-center text-xs text-djin-cyan hover:text-djin-cyan/80 py-1.5 border-t border-ems-border/40"
        >
          View all alarms →
        </Link>
      </div>
    </section>
  );
}

export default AlarmSummaryCard;
